<template>
  <div class="skeleton" :class="[`skeleton--${variant}`, { 'skeleton--pulse': pulse }]">
    <!-- Text skeleton -->
    <div v-if="variant === 'text'" class="skeleton__text" :style="{ width, height }"></div>
    
    <!-- Avatar skeleton -->
    <div v-else-if=" variant === 'avatar'" class="skeleton__avatar" :style="{ width: size + 'px', height: size + 'px' }"></div>
    
    <!-- Card skeleton -->
    <div v-else-if="variant === 'card'" class="skeleton__card">
      <div class="skeleton__card-header">
        <div class="skeleton__avatar" :style="{ width: '60px', height: '60px' }"></div>
        <div class="skeleton__card-title">
          <div class="skeleton__text" style="width: 70%; height: 20px; margin-bottom: 8px;"></div>
          <div class="skeleton__text" style="width: 40%; height: 14px;"></div>
        </div>
      </div>
      <div class="skeleton__card-body">
        <div class="skeleton__text" style="width: 100%; height: 14px; margin-bottom: 8px;"></div>
        <div class="skeleton__text" style="width: 80%; height: 14px;"></div>
      </div>
    </div>
    
    <!-- Championship card skeleton -->
    <div v-else-if="variant === 'championship'" class="skeleton__championship">
      <div class="skeleton__championship-header">
        <div class="skeleton__avatar" :style="{ width: '80px', height: '80px' }"></div>
        <div class="skeleton__badge"></div>
      </div>
      <div class="skeleton__championship-title">
        <div class="skeleton__text" style="width: 80%; height: 24px;"></div>
      </div>
      <div class="skeleton__championship-meta">
        <div class="skeleton__text" style="width: 60%; height: 16px;"></div>
      </div>
      <div class="skeleton__championship-footer">
        <div class="skeleton__text" style="width: 100%; height: 40px; border-radius: 8px;"></div>
      </div>
    </div>
    
    <!-- Table row skeleton -->
    <div v-else-if="variant === 'table-row'" class="skeleton__table-row">
      <div class="skeleton__avatar" :style="{ width: '32px', height: '32px' }"></div>
      <div class="skeleton__text" style="flex: 1; height: 16px; margin: 0 12px;"></div>
      <div class="skeleton__text" style="width: 30px; height: 16px;"></div>
    </div>
    
    <!-- Custom -->
    <div v-else class="skeleton__custom" :style="{ width, height }"></div>
  </div>
</template>

<script>
export default {
  name: 'SkeletonLoader',
  props: {
    variant: {
      type: String,
      default: 'text',
      validator: v => ['text', 'avatar', 'card', 'championship', 'table-row', 'custom'].includes(v)
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '16px'
    },
    size: {
      type: Number,
      default: 40
    },
    pulse: {
      type: Boolean,
      default: true
    }
  }
}
</script>

<style scoped>
.skeleton {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
}

.skeleton--pulse {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.skeleton__text {
  background: linear-gradient(90deg, 
    rgba(255,255,255,0.05) 0%, 
    rgba(255,255,255,0.1) 50%, 
    rgba(255,255,255,0.05) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton__avatar {
  background: linear-gradient(90deg, 
    rgba(255,255,255,0.05) 0%, 
    rgba(255,255,255,0.1) 50%, 
    rgba(255,255,255,0.05) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 50%;
}

.skeleton__custom {
  background: linear-gradient(90deg, 
    rgba(255,255,255,0.05) 0%, 
    rgba(255,255,255,0.1) 50%, 
    rgba(255,255,255,0.05) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

/* Card variant */
.skeleton__card {
  padding: 16px;
}

.skeleton__card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.skeleton__card-title {
  flex: 1;
}

.skeleton__card-body {
  display: flex;
  flex-direction: column;
}

/* Championship variant */
.skeleton__championship {
  padding: 20px;
  background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  border: 2px solid transparent;
}

.skeleton__championship-header {
  display: flex;
  justify-content: center;
  position: relative;
  margin-bottom: 16px;
}

.skeleton__badge {
  position: absolute;
  top: 0;
  right: 0;
  width: 60px;
  height: 24px;
  background: rgba(255,255,255,0.1);
  border-radius: 12px;
}

.skeleton__championship-title {
  margin-bottom: 12px;
}

.skeleton__championship-meta {
  margin-bottom: 16px;
}

.skeleton__championship-footer .skeleton__text {
  background: rgba(0, 212, 255, 0.2);
}

/* Table row variant */
.skeleton__table-row {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
</style>
